import lodash from 'lodash';
import { State } from '@autopylot-internal/state';
import { IMapViewModelPlugin } from '../map/model/IMapViewModelPlugin.ts';
import { MapViewModel } from '../map/model/MapViewModel.ts';
import { ICoordinate } from '@autopylot-internal/tiles-client';
import type { Map } from 'mapbox-gl';
import { ReverseGeocodingService } from '../map/location/ReverseGeocodingService.ts';

const MIN_ZOOM = 12;

/**
 * Lightweight map plugin that tracks the current map center for the search drawer weather component.
 * Weather fetching/display/modal is delegated to PreMissionWeatherState via PreMissionWeatherInDrawer.
 *
 * Location updates and reverse geocode lookups are debounced together (500ms) so that rapid
 * panning doesn't trigger API calls until the user has stopped moving. This single debounce
 * gates both the weather fetch (via PreMissionWeatherState) and the place name update.
 */
export class SearchDrawerWeatherState extends State<Record<string, never>> implements IMapViewModelPlugin {
    static inject = () => [ReverseGeocodingService];
    constructor(private reverseGeocoding: ReverseGeocodingService) {
        super({});
    }

    private map?: Map;
    private pendingLocation?: ICoordinate;
    private reverseLookupEnabled = false;

    location?: ICoordinate;
    placeName?: string;
    isZoomedOut = false;

    setReverseLookupEnabled = (enabled: boolean) => {
        this.reverseLookupEnabled = enabled;
        if (enabled && this.location && !this.placeName) {
            this.reverseLookup(this.location);
        }
    };

    init = (_mapViewModel: MapViewModel, map: Map) => {
        this.map = map;

        map.on('movestart', this.onMoveStart);
        map.on('moveend', this.onMoveEnd);

        this.sync();
    };

    sync = () => {
        if (!this.map) {
            return;
        }

        const center = this.map.getCenter();

        if (this.map.getZoom() < MIN_ZOOM) {
            this.location = undefined;
            this.placeName = undefined;
            this.isZoomedOut = true;
            this.setState({});
            return;
        }

        if (this.location && this.location.lat === center.lat && this.location.lng === center.lng) {
            return;
        }

        this.pendingLocation = { lat: center.lat, lng: center.lng };
        this.location = this.pendingLocation;
        this.isZoomedOut = false;
        this.setState({});

        if (this.reverseLookupEnabled) {
            this.reverseLookup(this.location);
        }
    };

    private onMoveStart = () => {
        this.syncWithDebounce.cancel();
    };

    private onMoveEnd = () => {
        if (!this.map) {
            return;
        }

        const center = this.map.getCenter();
        const zoomedOut = this.map.getZoom() < MIN_ZOOM;
        const centerUnchanged = this.pendingLocation && this.pendingLocation.lat === center.lat && this.pendingLocation.lng === center.lng;

        if (centerUnchanged && zoomedOut === this.isZoomedOut) {
            return;
        }

        this.pendingLocation = { lat: center.lat, lng: center.lng };
        this.syncWithDebounce();
    };

    private syncWithDebounce = lodash.debounce(() => {
        this.sync();
    }, 500, { leading: false });

    private reverseLookup = async (location: ICoordinate) => {
        try {
            const result = await this.reverseGeocoding.lookup(location);
            if (result) {
                const { city, state } = result.address;
                this.placeName = state ? `${city}, ${state}` : city;
            } else {
                this.placeName = undefined;
            }
        } catch {
            this.placeName = undefined;
        }
        this.setState({});
    };
}
